import get from 'lodash-es/get';

import { get as getLocationAutocomplete } from '@rover/react-lib/src/apis/locationAutocomplete.api';
import { get as getLocationDeviceGeolocation } from '@rover/react-lib/src/apis/locationDeviceGeolocation.api';
import { get as getLocationGeocode } from '@rover/react-lib/src/apis/locationGeocode.api';
import fetchLocationAutocompleteResponseFactory from '@rover/react-lib/src/factories/fetchLocationAutocompleteResponseFactory';
import fetchLocationGeocodeResponseFactory from '@rover/react-lib/src/factories/fetchLocationGeocodeResponseFactory';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import type {
  FetchLocationAutocompleteResponse,
  FetchLocationGeocodeResponse,
  FetchLocationResponse,
} from '@rover/types';

import './LocationInput.types';

import {
  CURRENT_LOCATION_DENIED_MESSAGE,
  CURRENT_LOCATION_OPTION,
} from './LocationInput.constants';
import type { SelectOption } from './LocationInput.types';
import { shouldShowCurrentLocation } from './utils';

type FetchLocationDeviceOrGeocodeResponse = FetchLocationGeocodeResponse | FetchLocationResponse;
type State = {
  data: Record<
    string,
    {
      errorMessage: string;
      fetchLocationAutocompleteResponse: FetchLocationAutocompleteResponse;
      fetchLocationGeocodeResponse: FetchLocationDeviceOrGeocodeResponse;
      loading: boolean;
      locationGeocodeResultsAreLoading: boolean;
      selectedOption?: SelectOption;
    }
  >;
};
type Actions = {
  fetchLocationAutocompletePending: ActionCreator<string>;
  fetchLocationAutocompleteSuccess: ActionCreator<{
    response: FetchLocationAutocompleteResponse;
    inputId: string;
  }>;
  fetchLocationAutocompleteFailure: ActionCreator<{
    errorMessage: string;
    inputId: string;
  }>;
  fetchLocationDeviceOrGeocodePending: ActionCreator<string>;
  fetchLocationDeviceOrGeocodeSuccess: ActionCreator<{
    response: FetchLocationDeviceOrGeocodeResponse;
    inputId: string;
  }>;
  fetchLocationDeviceOrGeocodeFailure: ActionCreator<{
    errorMessage: string;
    inputId: string;
  }>;
  setSelectedOption: ActionCreator<{
    selectedOption?: SelectOption | null;
    inputId: string;
  }>;
  dismissErrorMessage: ActionCreator<{
    inputId: string;
  }>;
};
type Effects = {
  fetchLocationAutocomplete: EffectCreator<{
    query: string;
    inputId: string;
    countryCode: string | null | undefined;
  }>;
  fetchLocationDeviceCurrentLocation: EffectCreator<{
    handleGeocodeError: () => void;
    inputId: string;
  }>;
  fetchLocationDeviceOrGeocode: EffectCreator<{
    currentLocationValue: string;
    guaranteePostalCode: boolean;
    handleGeocodeError: () => void;
    inputId: string;
    region?: string;
    selectedOption?: SelectOption;
  }>;
  fetchLocationGeocode: EffectCreator<{
    address?: string;
    guaranteePostalCode: boolean;
    handleGeocodeError: () => void;
    inputId: string;
    region?: string;
    placeId?: string;
  }>;
};
type Selectors = {
  getErrorMessage: SelectorCreator<string>;
  getIsLoading: SelectorCreator<boolean>;
  getLocationAutocompleteOptions: SelectorCreator<
    Array<{
      value: string;
      label: string;
    }>
  >;
  getLocationAutocompleteResponse: SelectorCreator<
    FetchLocationAutocompleteResponse | null | undefined
  >;
  getLocationGeocodeResponse: SelectorCreator<FetchLocationDeviceOrGeocodeResponse>;
  getLocationGeocodeResultsAreLoading: SelectorCreator<boolean>;
  getSelectedOption: SelectorCreator<SelectOption | undefined>;
};
type Options = DuckOptions<State, Actions, Effects, Selectors>;
const initialLocationState = {
  errorMessage: '',
  fetchLocationAutocompleteResponse: fetchLocationAutocompleteResponseFactory(),
  fetchLocationGeocodeResponse: fetchLocationGeocodeResponseFactory(),
  loading: false,
  locationGeocodeResultsAreLoading: false,
  selectedOption: null,
};

const updateLocationData = (state, updatedData, inputId) => ({
  ...state,
  data: {
    ...state.data,
    [inputId]: { ...initialLocationState, ...get(state, ['data', inputId], {}), ...updatedData },
  },
});

export const options: Options = {
  name: 'locationInput',
  initialState: {
    data: {},
  },
  actions: {
    fetchLocationAutocompletePending: (inputId) => (state) =>
      updateLocationData(
        state,
        {
          loading: true,
        },
        inputId
      ),
    fetchLocationAutocompleteSuccess:
      ({ response: locationAutocompleteResponse, inputId }) =>
      (state) =>
        updateLocationData(
          state,
          {
            loading: false,
            fetchLocationAutocompleteResponse: locationAutocompleteResponse,
            errorMessage: '',
          },
          inputId
        ),
    fetchLocationAutocompleteFailure:
      ({ errorMessage, inputId }) =>
      (state) =>
        updateLocationData(
          state,
          {
            loading: false,
            errorMessage,
          },
          inputId
        ),
    fetchLocationDeviceOrGeocodePending: (inputId) => (state) =>
      updateLocationData(
        state,
        {
          locationGeocodeResultsAreLoading: true,
        },
        inputId
      ),
    fetchLocationDeviceOrGeocodeSuccess:
      ({ response: locationGeocodeResponse, inputId }) =>
      (state) =>
        updateLocationData(
          state,
          {
            locationGeocodeResultsAreLoading: false,
            fetchLocationGeocodeResponse: locationGeocodeResponse,
            errorMessage: '',
          },
          inputId
        ),
    fetchLocationDeviceOrGeocodeFailure:
      ({ errorMessage, inputId }) =>
      (state) =>
        updateLocationData(
          state,
          {
            locationGeocodeResultsAreLoading: false,
            errorMessage,
          },
          inputId
        ),
    setSelectedOption:
      ({ selectedOption, inputId }) =>
      (state) =>
        updateLocationData(
          state,
          {
            selectedOption,
          },
          inputId
        ),
    dismissErrorMessage:
      ({ inputId }) =>
      (state) =>
        updateLocationData(
          state,
          {
            errorMessage: '',
          },
          inputId
        ),
  },
  effects: {
    fetchLocationAutocomplete:
      ({ query, inputId, countryCode }) =>
      async (
        dispatch,
        getState,
        {
          fetchLocationAutocompletePending,
          fetchLocationAutocompleteSuccess,
          fetchLocationAutocompleteFailure,
        }
      ) => {
        dispatch(fetchLocationAutocompletePending(inputId));

        try {
          const results = await getLocationAutocomplete({
            query,
            countryCode,
          });
          dispatch(
            fetchLocationAutocompleteSuccess({
              response: results,
              inputId,
            })
          );
        } catch (error: any) {
          dispatch(
            fetchLocationAutocompleteFailure({
              errorMessage: error.message,
              inputId,
            })
          );
          captureError(error);
        }
      },
    fetchLocationDeviceCurrentLocation:
      ({ handleGeocodeError, inputId }) =>
      async (
        dispatch,
        getState,
        {
          setSelectedOption,
          fetchLocationDeviceOrGeocodeFailure,
          fetchLocationDeviceOrGeocodePending,
          fetchLocationDeviceOrGeocodeSuccess,
        }
      ) => {
        dispatch(fetchLocationDeviceOrGeocodePending(inputId));

        try {
          const results: FetchLocationDeviceOrGeocodeResponse =
            await getLocationDeviceGeolocation();
          dispatch(
            fetchLocationDeviceOrGeocodeSuccess({
              response: results,
              inputId,
            })
          );
        } catch (error: any) {
          dispatch(
            setSelectedOption({
              selectedOption: null,
              inputId,
            })
          );
          dispatch(
            fetchLocationDeviceOrGeocodeFailure({
              errorMessage: CURRENT_LOCATION_DENIED_MESSAGE(),
              inputId,
            })
          );
          handleGeocodeError();
          captureError(error);
        }
      },
    fetchLocationDeviceOrGeocode:
      ({
        currentLocationValue,
        guaranteePostalCode,
        handleGeocodeError,
        inputId,
        region,
        selectedOption,
      }) =>
      async (
        dispatch,
        getState,
        { setSelectedOption },
        { fetchLocationDeviceCurrentLocation, fetchLocationGeocode }
      ) => {
        dispatch(
          setSelectedOption({
            selectedOption,
            inputId,
          })
        );
        const address = selectedOption?.value as string;
        const placeId = selectedOption?.placeId;
        // We don't have access to the i18n object in duck effects
        // in SSR. So instead of comparing to CURRENT_LOCATION_OPTION().value
        // directly, we need to translate it in a render function, and then
        // pass in the translated version here. This is not ideal.
        const shouldFetchDeviceCurrentLocation =
          shouldShowCurrentLocation() && address === currentLocationValue;

        if (shouldFetchDeviceCurrentLocation) {
          dispatch(
            fetchLocationDeviceCurrentLocation({
              handleGeocodeError,
              inputId,
            })
          );
        } else {
          dispatch(
            fetchLocationGeocode({
              address,
              guaranteePostalCode,
              handleGeocodeError,
              inputId,
              region,
              placeId,
            })
          );
        }
      },
    fetchLocationGeocode:
      ({ address, guaranteePostalCode, handleGeocodeError, inputId, region, placeId }) =>
      async (
        dispatch,
        getState,
        {
          fetchLocationDeviceOrGeocodeFailure,
          fetchLocationDeviceOrGeocodePending,
          fetchLocationDeviceOrGeocodeSuccess,
        }
      ) => {
        dispatch(fetchLocationDeviceOrGeocodePending(inputId));

        try {
          const results: FetchLocationDeviceOrGeocodeResponse = await getLocationGeocode({
            address,
            region,
            guaranteePostalCode,
            placeId,
          });
          dispatch(
            fetchLocationDeviceOrGeocodeSuccess({
              response: results,
              inputId,
            })
          );
        } catch (error: any) {
          dispatch(
            fetchLocationDeviceOrGeocodeFailure({
              errorMessage: error.message,
              inputId,
            })
          );
          handleGeocodeError();
          captureError(error);
        }
      },
  },
  selectors: {
    getErrorMessage: (getState, createSelector) =>
      createSelector([getState, (__, inputId) => inputId], (state, inputId) =>
        get(state, ['data', inputId, 'errorMessage'], '')
      ),
    getIsLoading: (getState, createSelector) =>
      createSelector([getState, (__, inputId) => inputId], (state, inputId) =>
        get(state, ['data', inputId, 'loading'], false)
      ),
    getLocationAutocompleteResponse: (getState, createSelector) =>
      createSelector([getState, (__, inputId) => inputId], (state, inputId) =>
        get(state, ['data', inputId, 'fetchLocationAutocompleteResponse'])
      ),
    getLocationAutocompleteOptions: (
      getState,
      createSelector,
      { getLocationAutocompleteResponse }
    ) =>
      createSelector(
        [
          getLocationAutocompleteResponse,
          (_, __, includeCurrentLocation) => includeCurrentLocation,
        ],
        (locationAutocompleteResponse, includeCurrentLocation) => {
          const predictions = get(locationAutocompleteResponse, ['predictions'], []);
          const predictionOptions = predictions.map((prediction) => ({
            label: prediction.description,
            value: prediction.description,
            placeId: prediction.placeId,
          }));

          if (shouldShowCurrentLocation() && includeCurrentLocation) {
            return [CURRENT_LOCATION_OPTION(), ...predictionOptions];
          }

          return predictionOptions;
        }
      ),
    getLocationGeocodeResponse: (getState, createSelector) =>
      createSelector(
        [getState, (__, inputId) => inputId],
        (state, inputId) =>
          get(state, ['data', inputId, 'fetchLocationGeocodeResponse']) ||
          fetchLocationGeocodeResponseFactory()
      ),
    getLocationGeocodeResultsAreLoading: (getState, createSelector) =>
      createSelector([getState, (__, inputId) => inputId], (state, inputId) =>
        get(state, ['data', inputId, 'locationGeocodeResultsAreLoading'], false)
      ),
    getSelectedOption: (getState, createSelector) =>
      createSelector([getState, (__, inputId) => inputId], (state, inputId) =>
        get(state, ['data', inputId, 'selectedOption'])
      ),
  },
};
export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
